/**
 * /realizacje - strona dowodu. Zamiast opisywac systemy, daje je kliknac.
 *
 * Konstrukcja: hero z liczbami LICZONYMI Z DANYCH -> siatka kafli -> CTA.
 * Naglowek strony i uklad skopiowane z UslugiPage.tsx dla spojnosci serwisu.
 *
 * Konfigurator gogli VR (Faza 3) wchodzi PONIZEJ pierwszego ekranu i montuje sie
 * dopiero po wejsciu w viewport. Powod w planie: canvas above the fold przewraca
 * Lighthouse i zjada pamiec.
 */
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useSeo } from '../lib/useSeo';
import RealizacjeGrid, { countByStatus, countClickable } from '../components/RealizacjeGrid';
import { PROJECTS } from '../data/realizacje';

/** JSON-LD z tej samej tablicy co kafle, zeby schema nie rozjechala sie z trescia. */
function itemListJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Realizacje i prototypy DAIEN',
    numberOfItems: PROJECTS.length,
    itemListElement: PROJECTS.map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: p.status === 'demo' ? `${p.title} (prototyp)` : p.title,
      description: p.description,
      ...(p.href?.startsWith('http') ? { url: p.href } : {}),
    })),
  };
}

export default function RealizacjePage() {
  useSeo({
    title: 'Realizacje i działające systemy - DAIEN',
    description:
      'Systemy, które zbudowałem i które działają na produkcji. Część otwierasz i klikasz bez zakładania konta. Przy każdej liczbie piszę skąd pochodzi.',
    path: '/realizacje',
  });

  const wdrozenia = countByStatus('wdrozenie');
  const dema = countByStatus('demo');
  const klikalne = countClickable();

  return (
    <div className="min-h-screen bg-bg text-text-primary">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListJsonLd()) }} />

      <header className="px-6 md:px-10 lg:px-16 pt-10 pb-6 max-w-[1200px] mx-auto">
        <Link
          to="/"
          className="text-sm text-muted hover:text-text-primary transition-colors inline-flex items-center gap-2"
        >
          ← DAIEN
        </Link>
      </header>

      {/* Hero */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <div className="flex items-center gap-4 mb-6">
            <span className="w-8 h-px bg-stroke" />
            <span className="text-xs text-muted uppercase tracking-[0.3em]">
              Realizacje i prototypy
            </span>
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl tracking-tight font-light mb-6 leading-[1.05]">
            Nie opowiadam. <span className="font-display italic">Pokazuję.</span>
          </h1>

          <p className="text-muted text-lg max-w-2xl leading-relaxed mb-8">
            Poniżej systemy, które zbudowałem. {klikalne} z nich otwierasz i klikasz od razu, bez
            zakładania konta. Reszta działa na produkcji, ale siedzi za logowaniem, bo w środku są
            dane klientów. Przy każdym piszę wprost, który jest który.
          </p>

          <ul className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm border-t border-stroke pt-6">
            <li className="text-muted">
              <span className="text-text-primary font-medium">{klikalne} do kliknięcia</span>
              <br />
              bez rejestracji
            </li>
            <li className="text-muted">
              <span className="text-text-primary font-medium">{wdrozenia} wdrożeń</span>
              <br />
              używanych przez kogoś na co dzień
            </li>
            <li className="text-muted">
              <span className="text-text-primary font-medium">{dema} własnych prototypów</span>
              <br />
              zbudowanych, żeby pokazać zakres
            </li>
          </ul>
        </motion.div>
      </section>

      <RealizacjeGrid />

      {/* CTA */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-24">
        <div className="border border-stroke rounded-2xl bg-surface p-8 md:p-12">
          <span className="text-xs text-muted uppercase tracking-[0.3em]">
            Chcesz taki system u siebie?
          </span>
          <h2 className="text-3xl md:text-4xl font-light tracking-tight mt-5 mb-4">
            Powiedz co Cię boli, <span className="font-display italic">policzę czy się opłaca.</span>
          </h2>
          <p className="text-muted max-w-xl leading-relaxed mb-8">
            Wdrożenia zaczynają się od 5 000 zł miesięcznie, systemy budowane pod zamówienie od
            25 000 zł. Jeśli Twój problem da się rozwiązać taniej niż moją pracą, powiem to wprost.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/#kontakt"
              className="inline-flex items-center justify-center gap-2 bg-text-primary text-bg px-8 py-4 rounded-full text-base font-medium hover:scale-[1.02] transition-transform"
            >
              Napisz do mnie
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/quiz"
              className="inline-flex items-center justify-center gap-2 border border-stroke text-text-primary px-8 py-4 rounded-full text-base font-medium hover:bg-bg transition-colors"
            >
              Policz ile tracisz teraz
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
